// src/components/ReservationCard.js
import React from 'react';
import { CalendarClock, Users, Clock, CheckCircle2, XCircle, UserCheck } from 'lucide-react';

// Formata a data de YYYY-MM-DD para DD/MM/YYYY
function formatToBrDate(dateString) {
    if (!dateString) return "Data não informada";
    if (dateString.includes("-")) {
        const [year, month, day] = dateString.split("-");
        return `${day}/${month}/${year}`;
    }
    return dateString;
}

function ReservationCard({ reservation }) {
    if (!reservation) return null;

    // Extraindo e normalizando as propriedades
    const id = reservation.ID || reservation.id;
    const date = formatToBrDate(reservation.Data || reservation.date);
    const time = reservation.Time || reservation.time;
    const type = reservation.Type || reservation.type || "Reserva";
    const rawStatus = reservation.Status || reservation.status || "Confirmada";
    
    // Calculando quantidade de pessoas baseada no array de Participants
    const participantsArray = reservation.Participants || reservation.participants;
    const peopleCount = participantsArray ? participantsArray.length : 1;

    const isRPG = type.toUpperCase() === 'RPG';

    // --- LÓGICA DE STATUS ---
    // Determinando qual configuração visual (cor, ícone, texto) usar baseado no status
    let statusConfig = {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        icon: CheckCircle2,
        label: 'Confirmada'
    };

    const normalizedStatus = rawStatus.toLowerCase();

    if (normalizedStatus === 'pendente' || normalizedStatus === 'parcial' || rawStatus === 'WAITING_PAYMENT') {
        statusConfig = {
            bg: 'bg-[#971253]/10',
            border: 'border-[#971253]/20',
            text: 'text-[#971253]',
            icon: Clock,
            label: 'Aguardando'
        };
    } else if (normalizedStatus === 'cancelada' || rawStatus === 'CANCELED') {
        statusConfig = {
            bg: 'bg-stone-100',
            border: 'border-stone-200',
            text: 'text-stone-500',
            icon: XCircle,
            label: 'Cancelada'
        };
    } else if (normalizedStatus.includes('checkin') || normalizedStatus.includes('check-in')) {
        statusConfig = {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            text: 'text-blue-700',
            icon: UserCheck,
            label: 'No Local'
        };
    }

    const IconComponent = statusConfig.icon;
    const isCanceled = statusConfig.label === 'Cancelada';
    
    return (
        <div className={`flex flex-col md:flex-row items-start md:items-center justify-between p-5 md:p-6 bg-white border border-[#dbcdac]/40 rounded-2xl shadow-sm hover:shadow-[0_8px_20px_-10px_rgba(82,138,137,0.2)] hover:border-[#528a89]/40 transition-all mb-4 gap-4 ${isCanceled ? 'opacity-70 grayscale-[30%]' : ''}`}>
            
            <div className="flex items-start md:items-center gap-4 md:gap-5 w-full md:w-auto">
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 ${isCanceled ? 'bg-stone-100 text-stone-400' : 'bg-[#528a89]/10 text-[#528a89]'}`}>
                    <CalendarClock size={24} />
                </div>
                
                <div className="flex-1">
                    {/* Tags / Metadados Superiores */}
                    <div className="flex items-center gap-2 mb-1.5">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${isRPG ? 'bg-purple-100 text-purple-700' : 'bg-[#EDF6F7] text-[#548C7E]'}`}>
                            {type}
                        </span>
                        <span className="text-xs text-stone-400 font-medium">#{id}</span>
                    </div>

                    {/* Data e Hora */}
                    <h4 className="font-serif text-lg md:text-xl font-bold text-stone-800 capitalize leading-tight mb-1">
                        {date} <span className="text-stone-300 font-normal mx-1">•</span> {time || "--:--"}
                    </h4>
                    
                    {/* Pessoas (Oculto se for RPG) */}
                    <div className={`flex items-center text-sm mt-2 ${isRPG ? 'invisible' : 'text-stone-500'}`}>
                        <span className="flex items-center gap-1.5">
                            <Users size={14} className={isCanceled ? 'text-stone-400' : 'text-[#dbcdac]'}/> {peopleCount} {peopleCount > 1 ? 'pessoas' : 'pessoa'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Badge de Status Dinâmico */}
            <div className="w-full md:w-auto flex justify-end">
                <span className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full text-center flex items-center gap-1.5 shadow-sm border ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text}`}>
                    <IconComponent size={14} /> {statusConfig.label}
                </span>
            </div>
        </div>
    );
}

export default ReservationCard;